import { Outlet, useLocation } from "react-router-dom";
import { SunMedium } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FloatingActions } from "@/components/layout/FloatingActions";
import { SEO } from "@/components/shared/SEO";
import { getSeoContent } from "@/data/seoContent";

export function SiteLayout({ siteData, isLoading, error, onRetry }) {
  const location = useLocation();
  const seo = getSeoContent(location.pathname);

  if (isLoading) {
    return (
      <div className="site-shell flex min-h-screen items-center justify-center px-6" data-testid="site-loading-screen">
        <div className="glass-panel max-w-xl space-y-5 p-10 text-left">
          <div className="flex items-center gap-3 text-primary">
            <SunMedium className="h-8 w-8" />
            <p className="text-sm font-semibold uppercase tracking-[0.28em]">Monarch Solar & Roofing Solutions</p>
          </div>
          <h1 className="text-4xl font-bold text-foreground" data-testid="site-loading-title">
            Building your lead-generation experience…
          </h1>
          <div className="h-2 overflow-hidden rounded-full bg-primary/10" data-testid="site-loading-progress">
            <div className="loading-bar h-full w-1/2 rounded-full bg-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !siteData) {
    return (
      <div className="site-shell flex min-h-screen items-center justify-center px-6" data-testid="site-error-screen">
        <div className="glass-panel max-w-lg space-y-5 p-10 text-left">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary" data-testid="site-error-eyebrow">
            Temporary loading issue
          </p>
          <h1 className="text-4xl font-bold text-foreground" data-testid="site-error-title">
            We couldn’t load the website content.
          </h1>
          <p className="text-base text-muted-foreground" data-testid="site-error-message">
            {error || "Please try again in a moment."}
          </p>
          <Button className="rounded-full px-6" data-testid="site-error-retry-button" onClick={onRetry}>
            Retry loading
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="site-shell min-h-screen" data-testid="site-layout-shell">
      <SEO description={seo.description} keywords={seo.keywords} pathname={location.pathname} schemaType={seo.schemaType} title={seo.title} />
      <Header siteData={siteData} />
      <main className="pb-28 pt-32 md:pt-36" data-testid="site-main-content">
        <Outlet context={{ siteData }} />
      </main>
      <Footer siteData={siteData} />
      <FloatingActions siteData={siteData} />
    </div>
  );
}